import { ReactElement, useEffect } from "react"
import styled from "styled-components"
import { useTranslation } from "../TranslationProvider"
import { zIndex } from "../lib/theme"
import { lockScroll, unlockScroll } from "../lib/scrollLock"

interface FullscreenImageViewerProps {
  src: string
  alt: string
  isOpen: boolean
  onClose: () => void
}

export default function FullscreenImageViewer({
  src,
  alt,
  isOpen,
  onClose,
}: FullscreenImageViewerProps): ReactElement | null {
  const { t } = useTranslation()

  // Handle scroll lock
  useEffect(() => {
    if (isOpen) {
      lockScroll()
    } else {
      unlockScroll()
    }

    return () => {
      unlockScroll()
    }
  }, [isOpen])

  // Handle keyboard navigation
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (!isOpen) return

      if (event.key === "Escape") {
        onClose()
      }
    }

    document.addEventListener("keydown", handleEscape)
    return () => {
      document.removeEventListener("keydown", handleEscape)
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <FullscreenOverlay onClick={onClose}>
      <CloseButton
        onClick={onClose}
        aria-label={t("accessibility.closeFullscreen")}>
        ×
      </CloseButton>
      <ImageContainer onClick={(e) => e.stopPropagation()}>
        <FullscreenImage src={src} alt={alt} />
      </ImageContainer>
    </FullscreenOverlay>
  )
}

const FullscreenOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: ${zIndex.modal};
  cursor: pointer;
  animation: fadeIn 0.3s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

const ImageContainer = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  cursor: default;
  display: flex;
  justify-content: center;
  align-items: center;
`

const CloseButton = styled.button`
  position: fixed;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background-color: var(--white);
  color: var(--tertiary);
  font-size: 24px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.2s ease;
  z-index: 1;
  user-select: none;

  &:hover {
    background-color: var(--white);
    transform: scale(1.1);
  }

  &:focus {
    outline: 2px solid var(--special);
    outline-offset: 2px;
  }

  @media only screen and (max-width: 600px) {
    top: 15px;
    right: 15px;
    width: 35px;
    height: 35px;
    font-size: 20px;
  }
`

const FullscreenImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 0.5rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  animation: scaleIn 0.3s ease-out;

  @keyframes scaleIn {
    from {
      transform: scale(0.8);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }
`
